import { useCallback, useContext, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { NetworkPortfolioManagementResourceView } from "@app/network-portfolio-management/interfaces";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

const VIEW_PARAM = "view";
// Classic Home links/bookmarks predate the generic `?view=` scheme and still emit `?archived=true`/`?hidden=true`.
// Accept them as aliases so those links resolve to the right view until the classic dashboard is retired.
const LEGACY_ARCHIVED_PARAM = "archived";
const LEGACY_HIDDEN_PARAM = "hidden";

// Roles permitted to enter each non-default view. The active view is always accessible; archived requires one
// of these roles. Hidden has no entry — it's the user's own per-user hidden list, open to every authenticated
// user. Adding a gated view = add its enum member + an entry here.
const VIEW_ACCESS_ROLES: Partial<Record<NetworkPortfolioManagementResourceView, Roles[]>> = {
  [NetworkPortfolioManagementResourceView.Archived]: [Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN],
};

export interface NPMView {
  /** The view currently shown, resolved from the URL and clamped to one the user may access. */
  getCurrentNpmView: () => NetworkPortfolioManagementResourceView;
  /** Whether the current user may access the given view. */
  canAccessNpmView: (view: NetworkPortfolioManagementResourceView) => boolean;
  /** Switch to the given view, syncing the `?view=` URL param (the active view clears it). */
  setNpmView: (view: NetworkPortfolioManagementResourceView) => void;
}

// Resolve the view from the URL: the generic `?view=` param wins, with legacy `?archived=true`/`?hidden=true`
// aliases for Classic Home links. Anything absent or unrecognized resolves to the default active view.
const parseView = (params: URLSearchParams): NetworkPortfolioManagementResourceView => {
  switch (params.get(VIEW_PARAM)) {
    case NetworkPortfolioManagementResourceView.Archived:
      return NetworkPortfolioManagementResourceView.Archived;
    case NetworkPortfolioManagementResourceView.Hidden:
      return NetworkPortfolioManagementResourceView.Hidden;
    default:
      if (params.get(LEGACY_ARCHIVED_PARAM) === "true") {
        return NetworkPortfolioManagementResourceView.Archived;
      }
      if (params.get(LEGACY_HIDDEN_PARAM) === "true") {
        return NetworkPortfolioManagementResourceView.Hidden;
      }
      return NetworkPortfolioManagementResourceView.Active;
  }
};

function useCanAccessNpmView(): (view: NetworkPortfolioManagementResourceView) => boolean {
  const { roles } = useContext(UserContext);
  return useCallback(
    (view: NetworkPortfolioManagementResourceView): boolean => {
      const required = VIEW_ACCESS_ROLES[view];
      return !required || required.some((role) => roles.includes(role));
    },
    [roles],
  );
}

/**
 * NPM dashboard view scope (active/archived/hidden). The URL is the source of truth — `?view=archived`
 * resolves directly from a bookmark or shared link — so a provider-less hook keeps every consumer in sync through
 * the router. A view the current user cannot access resolves to the active view (AC #3/#10).
 */
export const useNPMView = (): NPMView => {
  const history = useHistory();
  const location = useLocation();
  const canAccessNpmView = useCanAccessNpmView();

  const getCurrentNpmView = useCallback((): NetworkPortfolioManagementResourceView => {
    const requested = parseView(new URLSearchParams(location.search));
    return canAccessNpmView(requested) ? requested : NetworkPortfolioManagementResourceView.Active;
  }, [location.search, canAccessNpmView]);

  const setNpmView = useCallback(
    (view: NetworkPortfolioManagementResourceView): void => {
      const params = new URLSearchParams(history.location.search);
      // Drop the legacy aliases so the URL settles on the canonical `?view=` scheme once the user interacts.
      params.delete(LEGACY_ARCHIVED_PARAM);
      params.delete(LEGACY_HIDDEN_PARAM);
      if (view === NetworkPortfolioManagementResourceView.Active) {
        params.delete(VIEW_PARAM);
      } else {
        params.set(VIEW_PARAM, view);
      }
      const search = params.toString();
      history.push({ search: search ? `?${search}` : "" });
    },
    [history],
  );

  return { canAccessNpmView, getCurrentNpmView, setNpmView };
};

/**
 * Strips a `?view=` the current user cannot access so the dashboard resolves to the active view rather than
 * erroring (AC #10). Call once at the dashboard root — not from {@link useNPMView} — so the navigation side
 * effect fires a single time regardless of how many components read the view.
 */
export const useEnforceViewAccess = (): void => {
  const history = useHistory();
  const location = useLocation();
  const canAccessNpmView = useCanAccessNpmView();
  const requested = parseView(new URLSearchParams(location.search));
  const allowed = canAccessNpmView(requested);

  useEffect(() => {
    if (requested !== NetworkPortfolioManagementResourceView.Active && !allowed) {
      const params = new URLSearchParams(history.location.search);
      params.delete(VIEW_PARAM);
      params.delete(LEGACY_ARCHIVED_PARAM);
      params.delete(LEGACY_HIDDEN_PARAM);
      const search = params.toString();
      history.replace({ search: search ? `?${search}` : "" });
    }
    // `history` is a stable singleton in React Router v5, so it is intentionally omitted from the deps.
  }, [requested, allowed]);
};
